import { GetState } from '@/actions/actiontypes';
import { Data } from '@/bootstrap/data';
import {
    excludeAdvancedFilterNode,
    resolveAdvancedFilterContextForNode,
} from '@/domain/advancedFilter';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { statePossibleAdvancedFilterValuesSearchContext } from '@/selectors/selectors';
import { VegobjektEgenskaperResponse } from '@/server/responses';
import {
    MapState,
    OmraderState,
    VegnettState,
    VegobjekterState,
    VegobjekttypeState,
} from '@/state';
import { Dispatch } from '@/state/store';
import { Integration } from './integration';
import {
    CommonQueries,
    MapQueries,
    OmraderQueries,
    VegnettQueries,
    VegobjekterQueries,
} from './queries';

export const POSSIBLE_ADVANCED_FILTER_VALUES_MAX_PAGES_PER_BATCH = 5;
const CACHE_TTL_MS = 3 * 60 * 1000;
const CACHE_MAX_ENTRIES = 32;

export interface PossibleAdvancedFilterValuesSearchContext {
    mapState: MapState;
    omraderState: OmraderState;
    vegnettState: VegnettState;
    vegobjekterState: VegobjekterState;
    timestamp: Date | null;
}

interface PossibleAdvancedFilterValuesRequest {
    key: string;
    typeId: number;
    egenskapstypeId: number;
    mapQueries: MapQueries;
    omraderQueries: OmraderQueries;
    vegnettQueries: VegnettQueries;
    vegobjekterQueries: VegobjekterQueries;
    timestamp: Date | null;
}

export interface PossibleAdvancedFilterValuesResult {
    /** `stale` means the result no longer matches the search context and must be ignored. */
    readonly status: 'ok' | 'error' | 'stale';
    readonly contextKey: string | null;
    /** Distinct, sorted values found so far. */
    readonly values: string[];
    /** Opaque cursor for the next batch, or `null` when every page has been read. */
    readonly nextCursor: string | null;
}

export interface FetchPossibleAdvancedFilterValuesOptions {
    continueFrom?: PossibleAdvancedFilterValuesResult;
    signal?: AbortSignal;
}

type DataForSuggestions = Pick<Data, 'kommuner' | 'datakatalog'>;

interface CacheEntry {
    values: string[];
    nextCursor: string | null;
    expiresAt: number;
}

type BatchOutcome =
    | { kind: 'aborted' }
    | { kind: 'done'; fetched: string[]; nextCursor: string | null; failed: boolean };

interface Subscriber {
    isInterested: () => boolean;
}

interface PendingBatch {
    readonly id: string;
    readonly controller: AbortController;
    readonly subscribers: Set<Subscriber>;
    promise: Promise<BatchOutcome>;
}

const cache = new Map<string, CacheEntry>();
const pendingBatches = new Map<string, PendingBatch>();

/**
 * Builds the suggestion request for a clause. The clause itself is excluded from the
 * query, and the key is derived from compiled request parameters, so changing the
 * clause's own operator or value never changes its key.
 */
function resolveRequest(
    searchContext: PossibleAdvancedFilterValuesSearchContext,
    data: DataForSuggestions,
    vegobjekttype: VegobjekttypeState,
    nodeId: string,
    egenskapstype: Egenskapstype | null
): PossibleAdvancedFilterValuesRequest | null {
    const topLevelTypeId = vegobjekttype.typeId;
    if (!egenskapstype || topLevelTypeId === null) {
        return null;
    }

    const { vegobjekterState, mapState, omraderState, vegnettState, timestamp } =
        searchContext;
    const currentVegobjekttype =
        vegobjekterState.vegobjekttypeStates.find(
            state => state.typeId === topLevelTypeId
        ) ?? vegobjekttype;
    const filterContext = resolveAdvancedFilterContextForNode(
        currentVegobjekttype.getAdvancedFilterDraft(),
        nodeId,
        data.datakatalog.vegobjekttyper,
        topLevelTypeId
    );
    const typeId = filterContext.contextType?.id ?? topLevelTypeId;
    const excludedDraft = excludeAdvancedFilterNode(filterContext.contextRoot, nodeId);

    const baseQueries =
        typeId === topLevelTypeId
            ? vegobjekterState.typeToQueries(typeId)
            : new VegobjekterQueries(true);
    const vegobjekterQueries = baseQueries
        .withAdvancedFilter(excludedDraft)
        .withEgenskaper([]);
    const mapQueries = mapState.toQueries();
    const omraderQueries = omraderState.toOmradeQueries(data.kommuner);
    const vegnettQueries = vegnettState.filter;

    const key = JSON.stringify({
        typeId,
        egenskapstypeId: egenskapstype.id,
        map: mapQueries.toParams(),
        omrader: omraderQueries.toParams(),
        vegnett: vegnettQueries.toParams(),
        vegobjekter: vegobjekterQueries.toParams(),
        timestamp: timestamp?.toISOString() ?? null,
    });

    return {
        key,
        typeId,
        egenskapstypeId: egenskapstype.id,
        mapQueries,
        omraderQueries,
        vegnettQueries,
        vegobjekterQueries,
        timestamp,
    };
}

export function getPossibleAdvancedFilterValuesContextKey(
    searchContext: PossibleAdvancedFilterValuesSearchContext,
    data: DataForSuggestions,
    vegobjekttype: VegobjekttypeState,
    nodeId: string,
    egenskapstype: Egenskapstype | null
): string | null {
    return (
        resolveRequest(searchContext, data, vegobjekttype, nodeId, egenskapstype)?.key ??
        null
    );
}

function pruneCache(now: number): void {
    for (const [key, entry] of cache.entries()) {
        if (entry.expiresAt <= now) {
            cache.delete(key);
        }
    }
    while (cache.size > CACHE_MAX_ENTRIES) {
        const oldestKey = cache.keys().next().value;
        if (oldestKey === undefined) {
            break;
        }
        cache.delete(oldestKey);
    }
}

function getCached(key: string, now = Date.now()): CacheEntry | null {
    pruneCache(now);
    const entry = cache.get(key);
    if (!entry) {
        return null;
    }
    cache.delete(key);
    cache.set(key, entry);
    return entry;
}

function setCached(key: string, values: string[], nextCursor: string | null): void {
    const now = Date.now();
    cache.delete(key);
    cache.set(key, { values, nextCursor, expiresAt: now + CACHE_TTL_MS });
    pruneCache(now);
}

export function resetPossibleAdvancedFilterValuesCache(): void {
    cache.clear();
    for (const batch of pendingBatches.values()) {
        batch.controller.abort();
    }
    pendingBatches.clear();
}

function mergeValues(existing: string[], fetched: string[]): string[] {
    return Array.from(new Set([...existing, ...fetched])).sort();
}

function abortBatch(batch: PendingBatch): void {
    batch.controller.abort();
    if (pendingBatches.get(batch.id) === batch) {
        pendingBatches.delete(batch.id);
    }
}

async function readPages(
    request: PossibleAdvancedFilterValuesRequest,
    startCursor: string | null,
    integration: Integration,
    batch: PendingBatch
): Promise<BatchOutcome> {
    const fetched = new Set<string>();
    const isObsolete = () =>
        batch.controller.signal.aborted ||
        ![...batch.subscribers].some(subscriber => subscriber.isInterested());
    let cursor = startCursor;

    for (
        let page = 0;
        page < POSSIBLE_ADVANCED_FILTER_VALUES_MAX_PAGES_PER_BATCH;
        page += 1
    ) {
        if (isObsolete()) {
            abortBatch(batch);
            return { kind: 'aborted' };
        }

        const commonQueries =
            cursor === null ? new CommonQueries() : new CommonQueries().withStart(cursor);
        let fragment: VegobjektEgenskaperResponse;
        try {
            fragment = await integration.server.getVegobjekterEgenskaper(
                request.typeId,
                commonQueries,
                request.mapQueries,
                request.omraderQueries,
                request.vegnettQueries,
                request.vegobjekterQueries,
                request.timestamp,
                batch.controller.signal
            );
        } catch (error) {
            if (batch.controller.signal.aborted) {
                return { kind: 'aborted' };
            }
            console.error('Error fetching possible advanced filter values:', error);
            // Keep what was read so far; the failing cursor allows an explicit retry.
            return {
                kind: 'done',
                fetched: [...fetched],
                nextCursor: page === 0 && startCursor === null ? null : cursor,
                failed: true,
            };
        }

        if (isObsolete()) {
            abortBatch(batch);
            return { kind: 'aborted' };
        }

        for (const objekt of fragment.objekter) {
            for (const egenskap of objekt.egenskaper) {
                if (egenskap.id === request.egenskapstypeId && egenskap.verdi != null) {
                    fetched.add(egenskap.verdi.toString());
                }
            }
        }

        cursor =
            fragment.metadata.returnert > 0 && fragment.metadata.neste
                ? fragment.metadata.neste.start
                : null;
        if (cursor === null) {
            break;
        }
    }

    return { kind: 'done', fetched: [...fetched], nextCursor: cursor, failed: false };
}

function startBatch(
    batchId: string,
    request: PossibleAdvancedFilterValuesRequest,
    startCursor: string | null,
    baseValues: string[],
    integration: Integration,
    initiator: Subscriber
): PendingBatch {
    const batch: PendingBatch = {
        id: batchId,
        controller: new AbortController(),
        subscribers: new Set([initiator]),
        promise: Promise.resolve({ kind: 'aborted' }),
    };
    pendingBatches.set(batchId, batch);

    batch.promise = readPages(request, startCursor, integration, batch)
        .then(outcome => {
            const readAnyPage =
                outcome.kind === 'done' &&
                (!outcome.failed || outcome.nextCursor !== startCursor);
            if (outcome.kind === 'done' && readAnyPage) {
                setCached(
                    request.key,
                    mergeValues(baseValues, outcome.fetched),
                    outcome.nextCursor
                );
            }
            return outcome;
        })
        .finally(() => {
            if (pendingBatches.get(batchId) === batch) {
                pendingBatches.delete(batchId);
            }
        });

    return batch;
}

function waitForBatch(
    batch: PendingBatch,
    subscriber: Subscriber,
    signal: AbortSignal | undefined
): Promise<BatchOutcome> {
    batch.subscribers.add(subscriber);

    return new Promise<BatchOutcome>(resolve => {
        const onAbort = () => {
            batch.subscribers.delete(subscriber);
            if (batch.subscribers.size === 0) {
                abortBatch(batch);
            }
            resolve({ kind: 'aborted' });
        };

        if (signal?.aborted) {
            onAbort();
            return;
        }
        signal?.addEventListener('abort', onAbort, { once: true });

        void batch.promise.then(outcome => {
            signal?.removeEventListener('abort', onAbort);
            batch.subscribers.delete(subscriber);
            resolve(outcome);
        });
    });
}

export type fetchPossibleAdvancedFilterValuesT = (
    vegobjekttype: VegobjekttypeState,
    nodeId: string,
    egenskapstype: Egenskapstype | null,
    options?: FetchPossibleAdvancedFilterValuesOptions
) => (
    dispatch: Dispatch,
    getState: GetState,
    integration: Integration
) => Promise<PossibleAdvancedFilterValuesResult>;

/**
 * Fetches distinct values of a property for an advanced filter clause, reading at most
 * {@link POSSIBLE_ADVANCED_FILTER_VALUES_MAX_PAGES_PER_BATCH} pages per call. Pass a
 * previous partial result as `continueFrom` to read the next batch.
 */
export const fetchPossibleAdvancedFilterValues: fetchPossibleAdvancedFilterValuesT =
    (vegobjekttype, nodeId, egenskapstype, options = {}) =>
    async (_dispatch, getState, integration) => {
        const { continueFrom, signal } = options;
        const currentKey = () =>
            getPossibleAdvancedFilterValuesContextKey(
                statePossibleAdvancedFilterValuesSearchContext(getState()),
                integration.data,
                vegobjekttype,
                nodeId,
                egenskapstype
            );
        const request = resolveRequest(
            statePossibleAdvancedFilterValuesSearchContext(getState()),
            integration.data,
            vegobjekttype,
            nodeId,
            egenskapstype
        );

        const stale = (): PossibleAdvancedFilterValuesResult => ({
            status: 'stale',
            contextKey: request?.key ?? null,
            values: continueFrom?.values ?? [],
            nextCursor: null,
        });

        if (!request) {
            return { status: 'ok', contextKey: null, values: [], nextCursor: null };
        }

        if (continueFrom && continueFrom.contextKey !== request.key) {
            return stale();
        }
        if (continueFrom && continueFrom.nextCursor === null) {
            return { ...continueFrom, status: 'ok' };
        }

        if (!continueFrom) {
            const cached = getCached(request.key);
            if (cached) {
                return {
                    status: 'ok',
                    contextKey: request.key,
                    values: [...cached.values],
                    nextCursor: cached.nextCursor,
                };
            }
        }

        const baseValues = continueFrom?.values ?? [];
        const startCursor = continueFrom?.nextCursor ?? null;
        const batchId = `${request.key}\u0000${startCursor ?? ''}`;
        const subscriber: Subscriber = {
            isInterested: () => !signal?.aborted && currentKey() === request.key,
        };

        if (signal?.aborted) {
            return stale();
        }

        const batch =
            pendingBatches.get(batchId) ??
            startBatch(
                batchId,
                request,
                startCursor,
                baseValues,
                integration,
                subscriber
            );
        const outcome = await waitForBatch(batch, subscriber, signal);

        if (outcome.kind === 'aborted' || !subscriber.isInterested()) {
            return stale();
        }

        return {
            status: outcome.failed ? 'error' : 'ok',
            contextKey: request.key,
            values: mergeValues(baseValues, outcome.fetched),
            nextCursor: outcome.nextCursor,
        };
    };
